import type { AdminDepositReturn } from '@inflip/contracts/admin';
import {
  DEPOSIT_RETURN_KIND_LABELS,
  DEPOSIT_RETURN_STATUS_LABELS,
  DepositReturnStatus,
} from '@inflip/shared';
import { useState } from 'react';

import {
  type BankAccountInput,
  ReturnFields,
  bankOk,
  trimBank,
  won,
} from './confirm-deposit-dialog';
import { Button, Dialog, DialogFooter, ErrorBanner, PromptDialog } from './ui';
import { formatDateTime } from '../lib/time-zone';

/**
 * 입금 반환 내역 — 주문 대금이 아닌 입금(초과·부족·만료 후)을 돌려주는 기록.
 * 송금한 뒤 [반환 완료](메모 필수), 계좌가 틀렸으면 [계좌 수정](사유 필수). 취소는 없다 (받은 돈은 반드시 돌려준다).
 */
export function DepositReturnsSection({
  returns,
  busy,
  onComplete,
  onUpdateAccount,
}: {
  returns: AdminDepositReturn[];
  busy: boolean;
  /** 실패하면 오류 문구, 성공하면 null */
  onComplete(id: string, note: string): Promise<string | null>;
  onUpdateAccount(
    id: string,
    bankAccount: BankAccountInput,
    reason: string,
  ): Promise<string | null>;
}) {
  const [completing, setCompleting] = useState<AdminDepositReturn | null>(null);
  const [editing, setEditing] = useState<AdminDepositReturn | null>(null);
  // 실패하면 창을 열어 둔 채 오류를 창 안에 보여 준다 — 적은 메모·계좌를 잃지 않게
  const [dialogError, setDialogError] = useState<string | null>(null);

  if (returns.length === 0) return null;
  return (
    <div className="mt-5 border-t border-border/60 pt-4">
      <p className="text-sm font-bold">입금 반환</p>
      <p className="text-xs text-muted-foreground">
        주문 대금이 아닌 입금을 돌려줘요. 송금한 뒤 [반환 완료]를 눌러 주세요.
      </p>
      <div className="mt-1 divide-y divide-border/40">
        {returns.map((r) => {
          const pending = r.status === DepositReturnStatus.PENDING;
          return (
            <div key={r.id} className="flex flex-wrap items-center gap-2 py-2.5 text-sm">
              <span
                className={`text-xs font-bold ${pending ? 'text-amber-600' : 'text-emerald-600'}`}
              >
                {DEPOSIT_RETURN_STATUS_LABELS[r.status]}
              </span>
              <span className="font-semibold">{DEPOSIT_RETURN_KIND_LABELS[r.kind]}</span>
              <span className="font-bold tabular-nums">{won(r.amount)}</span>
              {r.receivedAmount !== r.amount && (
                <span className="text-xs text-muted-foreground tabular-nums">
                  (입금 {won(r.receivedAmount)})
                </span>
              )}
              <span className="text-xs text-muted-foreground">
                → {r.bankAccount.bank} {r.bankAccount.accountNumber} ({r.bankAccount.holder})
              </span>
              <span className="ml-auto flex gap-1.5">
                {pending && (
                  <>
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={busy}
                      onClick={() => {
                        setDialogError(null);
                        setEditing(r);
                      }}
                    >
                      계좌 수정
                    </Button>
                    <Button
                      size="sm"
                      disabled={busy}
                      onClick={() => {
                        setDialogError(null);
                        setCompleting(r);
                      }}
                    >
                      반환 완료
                    </Button>
                  </>
                )}
              </span>
              <p className="basis-full text-xs text-muted-foreground">
                입금자 {r.depositorName} · 사유 {r.reason} · {formatDateTime(r.createdAt)}
                {r.accountUpdateReason && ` · 계좌 수정 사유 ${r.accountUpdateReason}`}
                {r.completedAt &&
                  ` · 반환 ${formatDateTime(r.completedAt)}${r.completionNote ? ` (${r.completionNote})` : ''}`}
              </p>
            </div>
          );
        })}
      </div>

      {completing && (
        <CompleteDepositReturnDialog
          depositReturn={completing}
          busy={busy}
          error={dialogError}
          onSubmit={async (note) => {
            setDialogError(null);
            const failure = await onComplete(completing.id, note);
            if (failure) setDialogError(failure);
            else setCompleting(null);
          }}
          onClose={() => setCompleting(null)}
        />
      )}
      {editing && (
        <EditDepositReturnAccountDialog
          depositReturn={editing}
          busy={busy}
          error={dialogError}
          onSubmit={async (bankAccount, reason) => {
            setDialogError(null);
            const failure = await onUpdateAccount(editing.id, bankAccount, reason);
            if (failure) setDialogError(failure);
            else setEditing(null);
          }}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

/** 반환 완료 — 실제 송금한 뒤에만. 송금 메모(5자 이상) 필수, 원장에 반환이 기록되고 구매자에게 알림 */
export function CompleteDepositReturnDialog({
  depositReturn: r,
  busy,
  error,
  onSubmit,
  onClose,
}: {
  depositReturn: AdminDepositReturn;
  busy: boolean;
  /** 서버 오류 — 창 안에 보여 준다 */
  error?: string | null;
  onSubmit(note: string): void;
  onClose(): void;
}) {
  return (
    <PromptDialog
      title={`${won(r.amount)} 반환을 완료할까요?`}
      description={`${r.bankAccount.bank} ${r.bankAccount.accountNumber}(${r.bankAccount.holder})로 실제 송금했을 때만 완료하세요. 원장에 반환이 기록되고 구매자에게 알림이 가요.`}
      placeholder="송금 메모 (예: 10/15 국민 이체 완료)"
      submitLabel="반환 완료"
      minLength={5}
      busy={busy}
      onSubmit={onSubmit}
      onClose={onClose}
    >
      {error && (
        <div className="mt-4">
          <ErrorBanner message={error} />
        </div>
      )}
    </PromptDialog>
  );
}

/** 반환 계좌 수정 — 반환 대기 중에만, 수정 사유(5자 이상) 필수 */
export function EditDepositReturnAccountDialog({
  depositReturn: r,
  busy,
  error,
  onSubmit,
  onClose,
}: {
  depositReturn: AdminDepositReturn;
  busy: boolean;
  /** 서버 오류 — 버튼 위에 보여 준다 */
  error?: string | null;
  onSubmit(bankAccount: BankAccountInput, reason: string): void;
  onClose(): void;
}) {
  const [bank, setBank] = useState<BankAccountInput>(r.bankAccount);
  const [reason, setReason] = useState('');
  return (
    <Dialog label="반환 계좌 수정" title="반환 계좌 수정" onClose={onClose}>
      <p className="text-sm text-muted-foreground">
        {DEPOSIT_RETURN_KIND_LABELS[r.kind]} {won(r.amount)}을 돌려줄 계좌를 바꿔요.
      </p>
      <div className="mt-4">
        <ReturnFields
          reason={reason}
          onReason={setReason}
          bank={bank}
          onBank={setBank}
          reasonLabel="수정 사유 (5자 이상)"
          reasonPlaceholder="예: 구매자가 다른 계좌로 받기를 요청"
        />
      </div>
      <DialogFooter notice={error ? <ErrorBanner message={error} /> : undefined}>
        <Button variant="secondary" className="flex-1" onClick={onClose}>
          취소
        </Button>
        <Button
          className="flex-1"
          disabled={busy || reason.trim().length < 5 || !bankOk(bank)}
          onClick={() => onSubmit(trimBank(bank), reason.trim())}
        >
          계좌 수정
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
